import { Button } from '@shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@shared/ui/dialog';
import { dialogFooterClassName, cancelButtonClassName, primaryButtonClassName } from '@shared/ui/formStyles';
import { IDLE_TIMEOUT_MINUTES } from '@shared/constants/config';
import { useKeycloak } from './KeycloakProvider';
import { useIdleLogout } from '../hooks/useIdleLogout';

const formatCountdown = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Idle auto-logout for the signed-in app: mounted once in the protected shell (AppLayout), so it
 * never runs on the public pages. Renders nothing until the user has been idle long enough for the
 * warning, then a countdown with "Stay signed in" / "Sign out".
 *
 * Built on the Dialog primitives rather than ConfirmationDialog: that one treats Escape / an
 * outside click as Cancel, which here would sign out a user who just proved they are present.
 */
export function IdleTimeoutDialog() {
  const { extendSession, logoutForIdle } = useKeycloak();
  const { isWarningOpen, secondsLeft, staySignedIn, signOutNow } = useIdleLogout({
    onIdle: logoutForIdle,
    onStay: extendSession,
  });

  return (
    <Dialog
      open={isWarningOpen}
      onOpenChange={(open) => {
        if (!open) staySignedIn();
      }}
    >
      <DialogContent className="sm:max-w-[480px]" dataTestId="idle-timeout-dialog">
        <DialogHeader className="pb-1">
          <DialogTitle data-testid="idle-timeout-dialog-title">Are you still there?</DialogTitle>
        </DialogHeader>
        <DialogDescription className="mb-0" data-testid="idle-timeout-dialog-description">
          You&apos;ve been inactive for a while. You&apos;ll be signed out in{' '}
          <span className="font-semibold tabular-nums" data-testid="idle-timeout-dialog-countdown">
            {formatCountdown(secondsLeft)}
          </span>
          . Sessions end after {IDLE_TIMEOUT_MINUTES} minutes of inactivity.
        </DialogDescription>
        <DialogFooter className={`${dialogFooterClassName} !px-0`}>
          <Button
            type="button"
            variant="outline"
            onClick={signOutNow}
            className={cancelButtonClassName}
            data-testid="idle-timeout-dialog-sign-out"
          >
            Sign out
          </Button>
          <Button
            type="button"
            onClick={staySignedIn}
            className={primaryButtonClassName}
            data-testid="idle-timeout-dialog-stay"
          >
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
